import { fail } from './errors.js';
import type { Principal } from './rbac.js';

export const SESSION_STATUSES = ['SCHEDULED', 'COMPLETED', 'NO_SHOW', 'CANCELLED'] as const;
export type SessionStatus = (typeof SESSION_STATUSES)[number];
/** 취소 구분: 일반 취소 / 보강 예정(다른 날 다시 수업) / 다음 달로 이월 */
export const CANCEL_KINDS = ['PLAIN', 'MAKEUP', 'CARRYOVER'] as const;
export type CancelKind = (typeof CANCEL_KINDS)[number];

/** 회기(수업 일정). 출결은 바우처 부정결제 대조의 기준이므로 변경 이력을 모두 남긴다. */
export interface TherapySession {
  id: string;
  centerId: string;
  clientId: string;
  therapistId: string;
  program: string;
  voucherProgram?: string; // 바우처 사업명(없으면 비바우처)
  startAt: string;         // ISO (KST 오프셋 포함)
  endAt: string;
  status: SessionStatus;
  version: number;         // 낙관적 락
  voucherPaidAt?: string;  // 바우처 결제가 이 회기와 연결된 시각 → 이후 변경은 잠금
  editedAfterPayment?: boolean;
  cancelKind?: CancelKind; // status 가 CANCELLED 일 때만
  movedFrom?: string;      // 처음 잡혔던 시작 시각(옮긴 적이 있으면)
  movedAt?: string;
}

export interface AttendanceChange {
  sessionId: string;
  centerId: string;
  from: SessionStatus;
  to: SessionStatus;
  by: string;
  role: Principal['role'];
  reason?: string;
  at: string;
  afterPayment: boolean;
}

/**
 * 출결 변경.
 * - 버전 불일치 → CONFLICT (다른 사람이 먼저 수정)
 * - 바우처 결제 이후 → 센터장만, 사유 필수, editedAfterPayment 플래그(부정결제 점검 대상)
 * - 결제 이전이라도 COMPLETED 를 되돌릴 때는 사유 필수
 */
export function changeAttendance(
  session: TherapySession,
  to: SessionStatus,
  actor: Principal,
  opts: { expectedVersion: number; reason?: string; now: string; cancelKind?: CancelKind },
): { session: TherapySession; change: AttendanceChange } {
  if (!SESSION_STATUSES.includes(to)) fail('VALIDATION', '알 수 없는 출결 상태입니다.');
  if (opts.expectedVersion !== session.version) fail('CONFLICT', '다른 사용자가 먼저 수정했습니다. 새로고침 후 다시 시도하세요.');
  const cancelKind = to === 'CANCELLED' ? opts.cancelKind ?? 'PLAIN' : undefined;
  if (cancelKind && !CANCEL_KINDS.includes(cancelKind)) fail('VALIDATION', '알 수 없는 취소 구분입니다.');
  if (session.status === to && session.cancelKind === cancelKind) fail('VALIDATION', '이미 같은 상태입니다.');
  const reason = opts.reason?.trim() || undefined;
  const afterPayment = Boolean(session.voucherPaidAt);
  if (afterPayment) {
    if (actor.role !== 'CENTER_DIRECTOR' || actor.impersonation) fail('LOCKED', '바우처 결제가 끝난 회기는 원장님만 수정할 수 있습니다.');
    if (!reason) fail('VALIDATION', '결제 후 출결 변경은 사유가 필요합니다.');
  } else if (session.status === 'COMPLETED' && !reason) {
    fail('VALIDATION', '완료된 회기를 되돌리려면 사유를 입력하세요.');
  }
  return {
    session: { ...session, status: to, cancelKind, version: session.version + 1, editedAfterPayment: session.editedAfterPayment || afterPayment },
    change: {
      sessionId: session.id, centerId: session.centerId, from: session.status, to, by: actor.userId, role: actor.role,
      reason: reason?.slice(0, 200), at: opts.now, afterPayment,
    },
  };
}
